/**
 * Guides pédagogiques MPDS — Legacy (premiers protocoles rédigés).
 * Extraits de TeachingGuide.js pour cohérence avec les Lots 1-3.
 */
export const buildTeachingGuidesLegacy = ({ Heart, AlertTriangle, ShieldAlert, Clock, Users }) => ({

  douleur_thoracique: {
    title: 'Douleur Thoracique',
    subtitle: 'Guide pédagogique pour l’enseignement du déterminant MPDS',
    intro: `La douleur thoracique est l’un des appels les plus critiques reçus en centrale 911.
Derrière ce symptôme peut se cacher un infarctus du myocarde, une embolie pulmonaire, une dissection aortique,
mais aussi une simple douleur musculo-squelettique ou une attaque de panique. La capacité du répartiteur à
différencier rapidement ces présentations détermine littéralement la priorité ambulancière, la disponibilité de
défibrillateurs, et la survie du patient.`,
    stats: [
      { icon: Clock, text: 'Un infarctus non reperfusé perd ~30 millions de cellules cardiaques par minute.' },
      { icon: Heart, text: 'Au Canada, l’infarctus du myocarde tue 1 personne toutes les 7 minutes.' },
      { icon: Users, text: 'Près de 50 % des patients sous-estiment leur douleur thoracique et appellent trop tard.' },
    ],
    objectives: [
      'Reconnaître un arrêt cardio-respiratoire imminent (signes pré-arrêt).',
      'Différencier une douleur cardiaque typique d’une douleur musculo-squelettique ou anxieuse.',
      'Conduire un questionnement structuré sans céder à la panique du citoyen.',
      'Repérer les drapeaux rouges (irradiation, diaphorèse, dyspnée, syncope).',
      'Donner des consignes pré-arrivée utiles (aspirine, position, ne pas laisser seul).',
      'Maintenir la communication jusqu’à l’arrivée des services préhospitaliers.',
    ],
    questioning: {
      title: 'Qu’est-ce que le questionnement MPDS ?',
      body: `Le MPDS (Medical Priority Dispatch System) est un protocole standardisé de questionnement
développé par le Dr Jeff Clawson. Chaque déterminant suit une séquence de questions en entonnoir : on part
de la sécurité immédiate vers les détails cliniques. L’objectif n’est PAS de diagnostiquer — c’est d’ATTRIBUER
une priorité d’intervention en moins de 60 secondes après les informations critiques.`,
      principles: [
        'Une question à la fois — ne pas accumuler.',
        'Réponses fermées (oui/non) avant les ouvertes.',
        'Reformuler ce que dit le citoyen pour valider sa compréhension.',
        'Ne JAMAIS dire « pas de panique » — préférer « je suis avec vous, on avance ensemble ».',
        'Continuer à parler même si le citoyen panique — la voix calme du répartiteur stabilise.',
      ],
    },
    phases: [
      {
        n: 1,
        title: 'Contrôle initial de l’appel',
        why: 'Sans adresse, sans rappel, l’ambulance ne part pas — TOUT le reste du questionnement est secondaire si ces 5 champs ne sont pas captés.',
        how: [
          'Adresse exacte + appartement / étage / accès',
          'Numéro de rappel — répétez-le à voix haute',
          'Nom de l’appelant ET du patient (parfois différent)',
          'Âge approximatif (la prévalence cardiaque change drastiquement après 40-50 ans)',
          'Sexe (les femmes présentent des symptômes atypiques plus fréquemment)',
        ],
        pitfalls: [
          'Ne pas valider le numéro de rappel = perdre le contact si la ligne coupe',
          'Approximer l’adresse — « près du parc » ≠ adresse',
          'Confondre l’appelant avec le patient (souvent un proche)',
        ],
      },
      {
        n: 2,
        title: 'Conscience et respiration',
        why: 'C’est le triage vital — un inconscient ne respirant pas = RCR immédiate. Le reste du questionnement attend.',
        how: [
          '« Est-il conscient et vous parle-t-il en ce moment ? »',
          '« Respire-t-il normalement, ou difficilement ? »',
          'Si inconscient → basculer immédiatement vers protocole RCR',
        ],
        pitfalls: [
          'Accepter « il respire » sans demander la qualité (rapide ? sifflant ? agonique ?)',
          'Oublier que les râles agoniques (gasping) ne sont PAS de la respiration',
        ],
      },
      {
        n: 3,
        title: 'Description de la douleur',
        why: 'Permet de différencier cardiaque, embolique, péricardique, musculaire ou anxieuse.',
        how: [
          'OPQRST mental : Onset, Provocation, Quality, Region, Severity, Time',
          'Mots-clés cardiaques : « écrasement », « poids », « serrement », « étau »',
          'Mots-clés non cardiaques : « pique », « coup d’aiguille », « augmente en respirant »',
        ],
        pitfalls: [
          'Suggérer la réponse (« est-ce que ça serre ? »)',
          'Ne pas poser la question d’irradiation',
        ],
      },
      {
        n: 4,
        title: 'Irradiation et symptômes associés',
        why: 'Une douleur qui irradie au bras gauche, mâchoire, dos = drapeau cardiaque MAJEUR.',
        how: [
          '« La douleur reste-t-elle au même endroit ou voyage-t-elle ? »',
          '« Vers où voyage-t-elle ? »',
          'Demander spécifiquement : bras gauche, mâchoire, dos',
        ],
        pitfalls: [
          'Oublier le côté droit chez femmes/diabétiques',
          'Considérer l’irradiation au dos comme « probablement musculaire »',
        ],
      },
      {
        n: 5,
        title: 'Diaphorèse, dyspnée, nausée',
        why: 'Triade classique du syndrome coronarien aigu — sensibilité ↑.',
        how: [
          'Sueurs froides ? (très spécifique)',
          'Essoufflement au repos ?',
          'Nausée, vomissement ?',
          'Sensation de mort imminente (« sentiment de fin du monde ») ?',
        ],
        pitfalls: [
          'Confondre diaphorèse avec « il fait chaud ici »',
          'Ne pas demander si essoufflé AU REPOS (versus à l’effort)',
        ],
      },
      {
        n: 6,
        title: 'Antécédents cardiaques personnels',
        why: 'Patient avec antécédents = risque récidive 5× supérieur.',
        how: [
          'Infarctus antérieur ? Pontage ? Angioplastie ?',
          'Stents posés ? Quand ?',
          'Médicaments cardiaques (nitro, bêta-bloquants, anticoagulants)',
        ],
        pitfalls: [
          'Oublier de demander si la nitro a déjà été prise et si elle a soulagé',
        ],
      },
      {
        n: 7,
        title: 'Facteurs de risque',
        why: 'Modifie la probabilité pré-test — guide la priorité.',
        how: [
          'Hypertension, diabète, hypercholestérolémie',
          'Tabac actif',
          'Antécédents familiaux (parent < 55 ans)',
          'Obésité, sédentarité',
        ],
        pitfalls: [
          'Tout demander d’un coup → surcharge cognitive du citoyen',
        ],
      },
      {
        n: 8,
        title: 'Contexte d’apparition',
        why: 'Effort, repos, stress émotionnel — chacun oriente.',
        how: [
          'À quoi faisait-il quand ça a commencé ?',
          'Au repos = plus inquiétant (angor instable)',
          'À l’effort + soulagement au repos = angor stable',
          'Stress émotionnel récent ?',
        ],
        pitfalls: [
          'Sous-estimer la douleur survenant la nuit (très inquiétante)',
        ],
      },
      {
        n: 9,
        title: 'Réévaluation finale et surveillance',
        why: 'L’état peut changer entre le 1er et le 10e appel — détecter la dégradation.',
        how: [
          'Re-demander : conscience ? respiration ? couleur des lèvres ?',
          'Préparer le citoyen : « les ambulanciers arrivent dans X minutes »',
          'Garder en ligne — ne pas raccrocher',
        ],
        pitfalls: [
          'Raccrocher trop tôt par souci de fluidité du standard',
        ],
      },
      {
        n: 10,
        title: 'Synthèse — Anxiété vs Cardiaque',
        why: 'Décision finale — attribuer la priorité.',
        how: [
          'Cocher les drapeaux rouges objectifs',
          'Vérifier les critères d’anxiété (jeune, contexte stress, soulagement par respiration calme)',
          'Si doute → priorité supérieure (mieux vaut sur-prioriser)',
        ],
        pitfalls: [
          'Étiqueter trop vite « anxiété » chez femmes / diabétiques / personnes âgées',
        ],
      },
    ],
    citizen_behavior: {
      title: 'Comment se comporter avec un citoyen en détresse',
      tone: [
        'Voix calme, ferme, basse — pas robotique',
        'Pauses courtes — ne pas remplir tous les silences',
        'Respiration audible synchronisée — le citoyen calque inconsciemment son rythme sur le vôtre',
        'Vouvoiement professionnel — sauf si très jeune ou très âgé en panique',
      ],
      phrases_utiles: [
        '« Je suis avec vous, on va y arriver ensemble. »',
        '« Vous avez bien fait d’appeler. »',
        '« Je vais vous poser des questions importantes. Prenez votre temps pour répondre. »',
        '« L’ambulance est en route, je reste en ligne avec vous. »',
        '« Pouvez-vous me dire ce que vous ressentez en ce moment précis ? »',
      ],
      phrases_a_eviter: [
        '« Calmez-vous » — invalidant, contre-productif',
        '« Ça va aller » — promesse impossible à tenir',
        '« Ne paniquez pas » — focalise sur la panique',
        '« Êtes-vous sûr ? » — décrédibilise',
        'Tout langage médical jargonnant (« infarctus du myocarde antéro-septal »)',
      ],
      panique_gestion: [
        'Si le citoyen crie → baisser SON propre volume (mirror inversé)',
        'Si le citoyen hyperventile → guider une respiration 4-7-8 (inspirer 4 s, retenir 7 s, expirer 8 s)',
        'Si le citoyen pleure → laisser pleurer 5 secondes puis enchaîner doucement',
        'Si le citoyen est agressif → ne pas confronter, recentrer sur la victime',
      ],
    },
    red_flags: [
      'Douleur en serrement / écrasement / poids',
      'Irradiation bras gauche, mâchoire, dos',
      'Sueurs froides (diaphorèse)',
      'Dyspnée au repos',
      'Sentiment de mort imminente',
      'Pâleur extrême',
      'Antécédent cardiaque connu',
      'Patient > 50 ans avec facteurs de risque',
      'Femme avec présentation atypique (fatigue, nausée, dyspnée)',
      'Diabétique (les diabétiques font des « infarctus silencieux »)',
    ],
    pre_arrival: [
      'Position semi-assise (jamais allongé à plat — augmente la précharge cardiaque)',
      'Desserrer vêtements au cou et ceinture',
      'Calmer et rassurer — voix audible',
      'Si patient prend déjà de la nitro et glycémie stable → autoriser 1 dose sublinguale',
      'Si patient > 16 ans, conscient, pas allergique, pas saignement actif → ASPIRINE 162-325 mg À CROQUER (réduit mortalité de 23 %)',
      'Aucun aliment ni boisson',
      'Ne pas laisser seul — même pour aller chercher la carte d’assurance',
      'Déverrouiller la porte d’entrée pour les ambulanciers',
      'Préparer la liste de médicaments en cours',
    ],
    common_errors: [
      'Oublier l’adresse de rappel → catastrophe si la ligne coupe.',
      'Demander le récit complet avant les drapeaux rouges → temps perdu.',
      'Ne pas demander l’irradiation → manquer un infarctus typique.',
      'Étiqueter « anxiété » sans vérifier les facteurs de risque cardiaques.',
      'Ne pas re-évaluer à la fin → manquer une dégradation.',
      'Raccrocher trop tôt → priver le citoyen du soutien.',
      'Suggérer la réponse (« c’est bien un serrement, n’est-ce pas ? »).',
      'Confondre dyspnée à l’effort (chronique) et dyspnée au repos (aiguë).',
    ],
    discussion_questions: [
      'Une femme de 45 ans appelle pour « fatigue intense + nausée ». Quelle est la première question à poser pour exclure un infarctus atypique ?',
      'Un patient diabétique se plaint d’une « gêne au plexus » sans douleur. Que ferait-on ?',
      'Un patient de 28 ans, en pleine crise de panique, décrit une douleur thoracique. Comment différencier ?',
      'Une personne âgée tombe dans les pommes après s’être plainte d’une douleur thoracique. Que faire dans les 30 prochaines secondes ?',
      'Pourquoi NE PAS donner de nitro si TA basse ou si pris du Viagra dans les dernières 24 h ?',
    ],
    references: [
      'Clawson J. et al., MPDS Protocol 10 — Chest Pain, 13th Edition (IAED)',
      'Société canadienne de cardiologie — Lignes directrices STEMI 2024',
      'Heart and Stroke Foundation of Canada — Signs of a heart attack',
      'CASN/RNAO — Best Practice Guideline: Establishing Therapeutic Relationships',
    ],
  },

  morsures: {
    title: 'Morsures et Piqûres',
    subtitle: 'Guide pédagogique pour l’enseignement du déterminant MPDS',
    compactCommon: true,
    intro: `Les appels pour morsures et piqûres couvrent un éventail très large : du chien familier qui mordille
au choc anaphylactique foudroyant après piqûre d’abeille, en passant par les morsures humaines (les plus
infectieuses), les serpents, araignées, tiques (maladie de Lyme) et chauves-souris (risque rabique).
Le rôle du répartiteur est de **trier la gravité immédiate** (allergie, hémorragie, atteinte voies aériennes)
et d’**orienter la conduite à tenir** : nettoyer, comprimer, surveiller — tout en identifiant les situations qui
exigent une vaccination antirabique d’urgence ou un transport prioritaire pour anaphylaxie.`,
    stats: [
      { icon: Heart, text: 'Au Canada, ~500 000 morsures de chien par an — 1 % nécessite des points de suture.' },
      { icon: AlertTriangle, text: 'La morsure humaine est la plus infectieuse de toutes (flore buccale polymicrobienne).' },
      { icon: ShieldAlert, text: 'L’anaphylaxie tue en 5-15 minutes — l’épinéphrine doit être donnée en < 5 min.' },
      { icon: Clock, text: 'La rage est mortelle à 100 % une fois symptomatique — d’où l’urgence de la vaccination post-exposition.' },
    ],
    objectives: [
      'Distinguer une morsure bénigne d’une morsure à risque vital (cou, anaphylaxie, hémorragie).',
      'Identifier les signes d’anaphylaxie en cours et orienter l’EpiPen.',
      'Reconnaître les animaux à haut risque rabique au Québec (chauve-souris, renard, raton-laveur, mouffette).',
      'Évaluer la nécessité d’un rappel antitétanique.',
      'Donner les bons soins de plaie initiaux (savon + eau 15 min, surtout pas de produit irritant).',
      'Identifier les situations à signaler (chien errant, agression, animal domestique non vacciné).',
    ],
    phases: [
      {
        n: 2,
        title: 'Conscience, respiration et anaphylaxie',
        why: 'L’anaphylaxie peut survenir en quelques minutes — c’est la priorité #1 avant toute autre question. Œdème de la glotte = obstruction des voies aériennes.',
        how: [
          '« Est-il conscient et parle-t-il ? »',
          '« Sa voix est-elle changée, rauque ? Difficulté à respirer ou avaler ? »',
          '« A-t-il des plaques rouges (urticaire) qui apparaissent ailleurs sur le corps ? »',
          '« Sent-il son cou ou sa langue gonfler ? »',
          'Si OUI à un seul de ces signes → ANAPHYLAXIE — basculer urgence + EpiPen',
        ],
        pitfalls: [
          'Attendre que tous les signes apparaissent — l’anaphylaxie peut être monosymptomatique au début',
          'Sous-estimer une « simple démangeaison à distance du site » (signe précoce)',
          'Ne pas demander si EpiPen disponible immédiatement',
        ],
      },
      {
        n: 3,
        title: 'Type d’animal / d’insecte',
        why: 'Le risque rabique, infectieux et venimeux dépend totalement de l’espèce. Détermine la conduite post-exposition.',
        how: [
          'Chien : connu ou errant ? Vacciné ? Provoqué ou non ?',
          'Chat : domestique ou errant ? Griffure ou morsure ?',
          'Humain : agression ? Bagarre ?',
          'Chauve-souris : contact même sans morsure visible = vaccination antirabique d’office',
          'Serpent : couleur, taille, forme de la tête (triangulaire = vipère)',
          'Abeille, guêpe, frelon, fourmi : dard resté en place ?',
          'Araignée : recluse brune, veuve noire (rares au QC) ?',
          'Tique : combien de temps accrochée ? Région à risque (maladie de Lyme)',
          'Animal inconnu / sauvage non identifié',
        ],
        pitfalls: [
          'Oublier la chauve-souris : un contact bref durant le sommeil suffit pour la rage',
          'Ne pas demander si l’animal est dispo pour observation (10 jours pour chien/chat)',
          'Confondre piqûre de tique avec mésothérapie (la tique peut tomber après)',
        ],
      },
      {
        n: 4,
        title: 'Localisation de la morsure',
        why: 'Visage, cou, mains, organes génitaux = transport hospitalier obligatoire (esthétique, fonctionnel, vital).',
        how: [
          '« Où exactement la morsure se trouve-t-elle ? »',
          'Visage / cou : risque vasculaire, esthétique majeur',
          'Mains / doigts : tendons, articulations, infections graves',
          'Organes génitaux : urgence chirurgicale',
          'Membres / tronc : moins critique mais à évaluer',
        ],
        pitfalls: [
          'Sous-estimer une morsure à la main (risque infection tendineuse)',
          'Considérer une morsure au visage comme « mineure » parce qu’elle saigne peu',
        ],
      },
      {
        n: 5,
        title: 'Profondeur et saignement',
        why: 'Distingue plaie superficielle (soins domiciles) de plaie profonde (sutures, transport).',
        how: [
          'Plaie superficielle : peau rougie, dermabrasion',
          'Plaie profonde : muscle ou gras visible, bords écartés',
          'Saignement : compressible ou en jet ? (jet = artère)',
          'Plusieurs morsures ? Lacérations multiples ?',
        ],
        pitfalls: [
          'Une morsure peut être profonde même si elle ne saigne pas (dents pointues)',
          'Oublier de demander si la victime est sous anticoagulants',
        ],
      },
      {
        n: 6,
        title: 'Signes d’infection',
        why: 'Une morsure vue tardivement (> 24 h) avec rougeur/pus = infection installée — antibiotiques IV possibles.',
        how: [
          'Rougeur étendue autour de la plaie ?',
          'Chaleur locale ?',
          'Œdème important ?',
          'Pus ou écoulement ?',
          'Stries rouges remontant le long du membre ? (lymphangite — urgent)',
          'Fièvre ?',
          'Depuis combien de temps la morsure a-t-elle eu lieu ?',
        ],
        pitfalls: [
          'Confondre la réaction normale (rougeur jour 1-2) avec une vraie cellulite',
          'Ne pas reconnaître la lymphangite (« lignes rouges qui remontent »)',
        ],
      },
      {
        n: 7,
        title: 'État vaccinal',
        why: 'Détermine si on doit prévoir tétanos, antirabique ou rien.',
        how: [
          'Vaccin antitétanique à jour ? Date dernier rappel ?',
          'Si > 10 ans (ou > 5 ans + plaie souillée) → rappel à l’hôpital',
          'Animal connu et vacciné → pas d’antirabique',
          'Animal inconnu / chauve-souris / animal sauvage → antirabique d’office',
          'EpiPen : déjà utilisé pour réactions antérieures ? Disponible maintenant ?',
        ],
        pitfalls: [
          'Le patient dit « je suis à jour » sans vérifier — souvent erroné chez l’adulte',
          'Oublier l’antirabique post-exposition pour les chauves-souris',
        ],
      },
      {
        n: 8,
        title: 'Symptômes systémiques (venin / réaction)',
        why: 'Certains venins (serpent, araignée recluse) provoquent des symptômes systémiques à distance.',
        how: [
          'Nausée, vomissements ?',
          'Étourdissements, vertiges ?',
          'Crampes musculaires (veuve noire) ?',
          'Vision trouble (envenimement neurotoxique) ?',
          'Douleur disproportionnée par rapport à la lésion ? (recluse brune, nécrose)',
        ],
        pitfalls: [
          'Attribuer des symptômes systémiques au stress / panique',
        ],
      },
      {
        n: 9,
        title: 'Contexte et exposition',
        why: 'Permet l’épidémiologie + sécurité scène.',
        how: [
          'L’animal est-il encore présent / agressif ?',
          'Plusieurs victimes ?',
          'Animal domestique (peut être observé) ou errant (introuvable) ?',
          'Région boisée / camping ? (tiques, serpents)',
          'L’enfant a-t-il provoqué l’animal ? (chiens familiaux mordent souvent enfants familiers)',
        ],
        pitfalls: [
          'Ne pas évaluer la sécurité avant d’envoyer une équipe (animal toujours dangereux)',
        ],
      },
      {
        n: 10,
        title: 'Synthèse — Morsure bénigne vs Urgence',
        why: 'Décision finale.',
        how: [
          'Critères « bénin » : petite morsure connue + pas allergie + plaie superficielle propre',
          'Critères « urgence » : anaphylaxie, plaie profonde, visage/cou, animal sauvage',
          'En cas de doute → priorité supérieure',
        ],
        pitfalls: [
          'Banaliser une morsure de chat (infections fréquentes à Pasteurella)',
          'Étiqueter « banal » sans vérifier vaccination antitétanique',
        ],
      },
    ],
    citizen_behavior: {
      title: 'Comment se comporter avec un citoyen en détresse',
      tone: [
        'Voix calme, articulée — le citoyen est souvent paniqué (enfant qui hurle en arrière-plan, sang visible)',
        'Empathie immédiate : « C’est traumatisant de voir ça, je comprends. »',
        'Posture professionnelle : éviter les sons d’émotion (« oh non ! ») qui amplifient la panique',
        'Si enfant impliqué → s’adresser au parent, puis demander qu’il rassure l’enfant',
      ],
      phrases_utiles: [
        '« Vous avez bien fait d’appeler tout de suite. »',
        '« Je vais vous guider étape par étape pour les premiers soins. »',
        '« L’EpiPen peut sauver une vie — donnez-le maintenant si vous l’avez. »',
        '« Notez l’heure exacte de la piqûre, c’est important pour les ambulanciers. »',
        '« Si l’animal est connu, ne le tuez pas — il devra être observé 10 jours. »',
      ],
      phrases_a_eviter: [
        '« Ce n’est probablement rien » — minimise un risque potentiel',
        '« Sucez le venin » — méthode obsolète et dangereuse',
        '« Mettez de la glace sur une morsure de serpent » — peut aggraver',
        '« Faites-le vomir » (réaction allergique) — risque aspiration',
        '« Allez aux urgences par vous-même » — si suspicion anaphylaxie, ambulance obligatoire',
      ],
      panique_gestion: [
        'Si la victime est un enfant qui crie → demander au parent de l’éloigner du sang visible',
        'Si dard d’abeille encore en place → guider le retrait avec une carte rigide (pas de pince qui injecte le venin restant)',
        'Si serpent encore présent → reculer à 2 m, NE PAS tenter de tuer ou capturer',
        'Si chauve-souris dans la maison → la confiner dans une pièce, fenêtre fermée, en attendant pompiers',
      ],
    },
    red_flags: [
      'Difficulté à respirer / voix rauque / langue gonflée',
      'Urticaire généralisée',
      'Pâleur, sueurs, malaise — choc anaphylactique débutant',
      'Saignement en jet pulsatile',
      'Morsure au visage, cou, organes génitaux',
      'Chauve-souris (même sans morsure visible)',
      'Animal sauvage non identifié',
      'Plaie profonde > 1 cm avec structures visibles',
      'Stries rouges remontant le long du membre (lymphangite)',
      'Symptômes systémiques (vertiges, vomi, crampes) après venin',
    ],
    pre_arrival: [
      'Si anaphylaxie suspectée → administrer EpiPen IMMÉDIATEMENT (cuisse externe, à travers les vêtements)',
      'Position semi-assise si difficulté respiratoire ; allongé jambes surélevées si choc/pâleur',
      'Compression directe ferme sur le saignement actif',
      'Laver à grande eau + savon doux 10-15 min (sauf morsure de serpent au membre — voir ci-dessous)',
      'Morsure de serpent au membre : immobiliser le membre EN POSITION BASSE, NE PAS sucer, NE PAS garrotter, NE PAS inciser',
      'Retirer le dard d’abeille en grattant avec une carte (jamais à la pince)',
      'Tique : retirer à l’aide d’une pince fine au plus près de la peau, sans tordre, conserver pour analyse',
      'Conserver la peau autour de la morsure d’araignée recluse — la photographier',
      'Ne PAS faire boire la victime en cas de réaction allergique sévère',
      'Si possible, sécuriser et identifier l’animal (sans s’y exposer) — pour l’observation rabique',
    ],
    common_errors: [
      'Sous-estimer une morsure de chat (Pasteurella → infection en 24 h).',
      'Ne pas reconnaître une anaphylaxie en cours (symptômes initiaux subtils).',
      'Oublier d’interroger sur l’EpiPen dispo / utilisé.',
      'Conseiller des remèdes obsolètes (sucer venin, glace sur serpent, eau de Javel).',
      'Banaliser le contact avec une chauve-souris « parce qu’il n’y a pas de marque ».',
      'Confondre rougeur normale (jour 1-2) avec une vraie cellulite.',
      'Ne pas demander la vaccination antitétanique.',
      'Oublier d’interroger sur les médicaments anticoagulants.',
    ],
    discussion_questions: [
      'Un enfant de 4 ans s’est fait mordre au visage par le chien familial. Le chien est vacciné. Conduite ?',
      'Une femme appelle car elle s’est réveillée avec une chauve-souris dans sa chambre, sans douleur ni marque. Pourquoi vaccination antirabique systématique ?',
      'Un homme se fait piquer par une abeille pour la 1re fois. Il se sent étourdi 5 minutes plus tard. Action ?',
      'Un randonneur a vu un serpent (« petit, foncé ») le mordre à la cheville en forêt québécoise. Que faire ? (Spoiler : seules vipères au QC = milieu très contrôlé, mais protocole identique.)',
      'Un patient sous Coumadin se fait mordre au mollet par un chien errant. Pourquoi est-ce plus grave ?',
      'Comment expliquer à un parent paniqué que NE PAS tuer le chien est important ?',
    ],
    references: [
      'Clawson J. et al., MPDS Protocol 3 — Animal Bites/Attacks, 13th Edition (IAED)',
      'Direction de santé publique du Québec — Guide d’intervention santé publique : rage post-exposition',
      'Centre antipoison du Québec : 1-800-463-5060 (24/7 — venins, identification d’insectes)',
      'INSPQ — Lignes directrices anaphylaxie et auto-injecteur d’épinéphrine',
      'Société canadienne de pédiatrie — Morsures animales chez l’enfant',
    ],
  },

});
